import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  MagnifyingGlassIcon,
  SparklesIcon,
  DocumentTextIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { aiReportsService, AIReport, ChartConfig } from '../../services/aiReportsService';

const COLORS = ['#6366f1', '#22c55e', '#eab308', '#ef4444', '#a855f7', '#14b8a6', '#f97316', '#ec4899'];

export default function AIReportsPage() {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string | AIReport }[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: suggestionsData } = useQuery({
    queryKey: ['ai-report-suggestions'],
    queryFn: () => aiReportsService.getSuggestions(),
  });

  const suggestions = suggestionsData?.data?.data || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isGenerating) return;

    const userQuery = query.trim();
    setQuery('');
    setIsGenerating(true);

    setMessages(prev => [...prev, { role: 'user', content: userQuery }]);

    try {
      const response = await aiReportsService.generate(userQuery);
      if (response.data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.data }]);
      }
    } catch (error: any) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${error.response?.data?.message || 'Failed to generate report'}` 
      }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSuggestionClick = (suggestionQuery: string) => {
    setQuery(suggestionQuery);
  };

  const renderChart = (chart: ChartConfig) => {
    const chartData = chart.config.labels.map((label, i) => ({
      name: label,
      value: chart.config.datasets[0]?.data[i] || 0,
    }));

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 60 },
    };

    switch (chart.type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }} 
                angle={-45} 
                textAnchor="end"
                interval={0}
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value: number) => `KSh ${value.toLocaleString()}`}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                name={chart.title}
                stroke="#6366f1" 
                strokeWidth={2}
                dot={{ fill: '#6366f1', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }} 
                angle={-45} 
                textAnchor="end"
                interval={0}
                height={80}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value: number) => `KSh ${value.toLocaleString()}`}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Legend />
              <Bar 
                dataKey="value" 
                name={chart.title}
                fill="#6366f1" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
      case 'doughnut':
        return (
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => `KSh ${value.toLocaleString()}`}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Suggestions Sidebar */}
      <div className="w-72 bg-white border-r border-gray-200 p-4 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <SparklesIcon className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900">Quick Queries</h3>
        </div>
        <div className="space-y-2">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              onClick={() => handleSuggestionClick(suggestion.query)}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <div className="text-sm font-medium text-gray-900">{suggestion.title}</div>
              <div className="text-xs text-gray-500 mt-1">{suggestion.category}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MagnifyingGlassIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl text-gray-500 font-medium">Ask me anything about your data</p>
                <p className="text-gray-400 mt-2">
                  Try: "How are sales trending this month?"
                </p>
              </div>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto space-y-6">
              {messages.map((message, index) => (
                <div key={index} className={message.role === 'user' ? 'text-right' : ''}>
                  {message.role === 'user' ? (
                    <div className="inline-block bg-primary-600 text-white px-4 py-2 rounded-lg rounded-br-none max-w-xl">
                      {message.content as string}
                    </div>
                  ) : typeof message.content === 'string' ? (
                    <div className="inline-block bg-red-50 text-red-800 px-4 py-2 rounded-lg">
                      {message.content}
                    </div>
                  ) : (
                    <div className="space-y-4 text-left">
                      {/* Analysis Summary */}
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-blue-900">Analysis</h3>
                        </div>
                        <p className="text-blue-800">{message.content.analysis.summary}</p>
                      </div>

                      {/* Key Insights */}
                      {message.content.analysis.insights.length > 0 && (
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <SparklesIcon className="w-5 h-5 text-green-600" />
                            <h4 className="font-semibold text-green-900">Key Insights</h4>
                          </div>
                          <ul className="list-disc list-inside space-y-1 text-green-800">
                            {message.content.analysis.insights.map((insight, i) => (
                              <li key={i}>{insight}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Recommendation */}
                      {message.content.analysis.recommendation && (
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <ArrowsPointingOutIcon className="w-5 h-5 text-purple-600" />
                            <h4 className="font-semibold text-purple-900">Recommendation</h4>
                          </div>
                          <p className="text-purple-800">{message.content.analysis.recommendation}</p>
                        </div>
                      )}

                      {/* Charts */}
                      {message.content.charts && message.content.charts.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-4">Visualizations</h4>
                          <div className={`grid gap-6 ${message.content.charts.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                            {message.content.charts.map((chart) => (
                              <div key={chart.title} className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-medium text-gray-700 mb-3 text-center">{chart.title}</h5>
                                {renderChart(chart)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* SQL Query
                      <details className="bg-gray-100 rounded-lg">
                        <summary className="font-medium text-gray-700 cursor-pointer p-3 hover:bg-gray-200 rounded-lg">
                          SQL Query
                        </summary>
                        <pre className="mt-2 text-xs overflow-auto bg-gray-800 text-green-400 p-4 rounded-b-lg max-h-64">
                          {message.content.sql_query}
                        </pre>
                      </details> */}

                      {/* Metadata */}
                      <div className="text-xs text-gray-500 flex items-center gap-4">
                        <span>Query time: {message.content.metadata.query_time_ms}ms</span>
                        <span>Data points: {message.content.metadata.data_points}</span>
                        <span>Charts: {message.content.metadata.charts_generated}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isGenerating && (
                <div className="flex items-center gap-3 text-gray-500">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                  <span>Analyzing your query...</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input Form */}
        <div className="bg-white border-t border-gray-200 p-4">
          <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
            <div className="flex gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask me anything... e.g., 'Show me sales by category this month'"
                disabled={isGenerating}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                maxLength={500}
              />
              <button
                type="submit"
                disabled={isGenerating || !query.trim()}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {isGenerating ? 'Analyzing...' : 'Ask'}
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-500 text-right">
              {query.length}/500 characters
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
